import React, { useState } from 'react';
import { useLegacyImportSetting } from '../components/records/legacyImport';

// Management → System → Admin Settings → Legacy Import tab. Rendered as a
// panel inside SettingsAdmin.js, which carries the page chrome and the
// admin:settings gate.
//
// Two switches for the "Upload signed form" mode:
//   · Upload signed form — while on, the Management record forms for QC6 / QC7
//     and QC35 Form offer uploading the signed PDF instead of filling in the
//     whole form. Historical forms are loaded with it on; it goes off again
//     afterwards so new records keep being filled in online.
//   · Allow records without the signed form — while on (and the first switch
//     is on), an imported record may be created before its signed PDF is at
//     hand. It reads "Signed form pending" until an administrator uploads the
//     first version from Edit on the record. Meant for a one-off bulk load
//     whose paper forms are still being gathered.

const Switch = ({ checked, disabled, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${checked ? 'bg-blue-600' : 'bg-slate-300'}`}
  >
    <span className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
  </button>
);

const SettingsLegacyImport = () => {
  const { enabled, allowMissingSignedForm, loading, error, update } = useLegacyImportSetting();
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const save = async (body) => {
    setBusy(true); setSaveError(null);
    try { await update(body); } catch (e) { setSaveError(e.message); }
    setBusy(false);
  };

  const pendingActive = enabled === true && allowMissingSignedForm;

  return (
    <div className="max-w-5xl">
      <p className="text-sm text-slate-500 mb-4">
        Bring forms that were signed outside the system into Management: upload the signed PDF (or add it later) and answer only what the system acts on.
      </p>

      {/* ── Switch 1: the mode itself ─────────────────────────────────── */}
      <section className={`rounded-lg border p-5 sm:p-6 flex flex-wrap items-center gap-5 ${enabled ? 'bg-blue-50 border-blue-200' : 'bg-white border-slate-200'}`}>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-slate-900">Upload Signed Form</h2>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${enabled ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
              {loading ? 'Loading…' : enabled ? 'On' : 'Off'}
            </span>
          </div>
          <p className="text-sm text-slate-600 mt-1">
            {enabled
              ? 'The New record forms for QC6 / QC7 and QC35 Form offer “Upload signed form”. Switch it off once the historical forms are loaded.'
              : 'New records are filled in online. Switch on to load historical signed forms; records already imported stay as they are.'}
          </p>
          {(error || saveError) && (
            <p className="text-xs text-red-600 mt-2">{error || saveError}</p>
          )}
        </div>
        <Switch checked={enabled === true} disabled={loading || busy} onChange={(v) => save({ enabled: v })} />
      </section>

      {/* ── Switch 2: records without the signed form ─────────────────── */}
      <section className={`mt-3 rounded-lg border p-5 sm:p-6 flex flex-wrap items-center gap-5 ${pendingActive ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`}>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-slate-900">Allow Records Without the Signed Form</h2>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${pendingActive ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
              {loading ? 'Loading…' : allowMissingSignedForm ? (enabled ? 'On' : 'On — inactive until Upload signed form is on') : 'Off'}
            </span>
          </div>
          <p className="text-sm text-slate-600 mt-1">
            {allowMissingSignedForm
              ? 'An imported QC6 / QC7 or QC35 record may be created before its signed PDF is at hand. It reads “Signed form pending” everywhere a record appears until an administrator uploads the first version from Edit on the record. Switch off once the bulk load is done.'
              : 'Every imported record must carry its signed PDF when it is created. Switch on only for a bulk load whose paper forms are still being gathered — the records are then flagged “Signed form pending” until the PDF is uploaded.'}
          </p>
        </div>
        <Switch checked={allowMissingSignedForm === true} disabled={loading || busy} onChange={(v) => save({ allowMissingSignedForm: v })} />
      </section>

    </div>
  );
};

export default SettingsLegacyImport;
